import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { SubmissionDraftContext } from "../context/SubmissionDraftContext";
import { buildSubmissionPdfBlob } from "../utils/pdf";
import { fetchPublicIpv4Meta } from "../utils/network";
import { dataUrlToFile, removeImageBackground } from "../utils/signature";
import { isEmail } from "../utils/validators";
import { COUNTRY_OPTIONS } from "../data/submissionData";
import { parsePhoneNumberFromString } from "libphonenumber-js";

function SignatureCanvas({ onClose, onSave }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const isDarkMode = document.body.classList.contains("night-mode");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = isDarkMode ? "#0f0f0f" : "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = isDarkMode ? "#ffffff" : "#111827";
  }, [isDarkMode]);

  const getPosition = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches ? event.touches[0] : event;
    // Map from CSS pixels (clientX/clientY) to canvas bitmap pixels.
    // This fixes cursor/line misalignment when the canvas is scaled via CSS.
    const scaleX = rect.width ? canvas.width / rect.width : 1;
    const scaleY = rect.height ? canvas.height / rect.height : 1;
    return {
      x: (source.clientX - rect.left) * scaleX,
      y: (source.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (event) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    const { x, y } = getPosition(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawingRef.current = true;
  };

  const draw = (event) => {
    if (!drawingRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    const { x, y } = getPosition(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    drawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.fillStyle = isDarkMode ? "#0f0f0f" : "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    const file = dataUrlToFile(dataUrl, "drawn-signature.png");
    const transparentDataUrl = await removeImageBackground(file);
    onSave(transparentDataUrl);
  };

  return (
    <div className="signature-modal-overlay">
      <div className="signature-modal">
        <h3>Draw Signature</h3>
        <canvas
          ref={canvasRef}
          width={640}
          height={260}
          className="signature-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="wizard-actions">
          <button className="toolbar-btn" onClick={clearCanvas}>
            Clear
          </button>
          <button className="toolbar-btn" onClick={onClose}>
            Close
          </button>
          <button className="toolbar-btn primary-btn" onClick={saveSignature}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Signature() {
  const CUSTOM_JOURNAL_VALUE = "__custom__";
  const navigate = useNavigate();
  const { createSubmission, fetchMySubmissions } = useContext(AuthContext);
  const { draft, updateDraft, resetDraft } = useContext(SubmissionDraftContext);

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [drawModalOpen, setDrawModalOpen] = useState(false);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    setError("");
    setMessage("");
    try {
      const processedSignature = await removeImageBackground(file);
      updateDraft({
        signatureMode: "upload",
        signatureDataUrl: processedSignature
      });
      setMessage("Signature uploaded and background removed successfully.");
    } catch {
      setError("Unable to process signature image.");
    } finally {
      setUploading(false);
    }
  };

  const saveDrawnSignature = (signatureDataUrl) => {
    updateDraft({
      signatureMode: "draw",
      signatureDataUrl
    });
    setDrawModalOpen(false);
    setMessage("Signature drawn and saved successfully.");
    setError("");
  };


  const handleSubmit = async () => {
    const authors = Array.isArray(draft.authors) ? draft.authors : [];
    if (!authors.length || !authors.some((author) => author.name?.trim())) {
      setError("Author details are mandatory. Please add at least one author.");
      return;
    }
    if (!draft.correspondenceName?.trim() || !draft.correspondenceEmail?.trim()) {
      setError("Correspondence author and email are required.");
      return;
    }
    if (!isEmail(draft.correspondenceEmail)) {
      setError("Correspondence email must be valid.");
      return;
    }

    if (!draft.countryCode?.trim() || !draft.phone?.trim() || !draft.address?.trim() || !draft.country?.trim()) {
      setError("Contact details are mandatory and must be valid.");
      return;
    }
    const selectedCountry =
      COUNTRY_OPTIONS.find((item) => item.name === draft.country) ||
      COUNTRY_OPTIONS.find((item) => item.dialCode === draft.countryCode);
    const parsedPhone = selectedCountry
      ? parsePhoneNumberFromString(draft.phone.trim(), selectedCountry.code)
      : null;
    if (!selectedCountry || !parsedPhone || !parsedPhone.isValid() || draft.address.trim().length < 10) {
      setError("Contact details are mandatory and must be valid.");
      return;
    }

    const finalJournalName = (
      draft.journalName === CUSTOM_JOURNAL_VALUE
        ? draft.journalNameManual
        : draft.journalNameManual || draft.journalName
    || "").trim();
    if (
      !draft.msId?.trim() ||
      draft.msId.trim().length < 3 ||
      !draft.msTitle?.trim() ||
      draft.msTitle.trim().length < 5 ||
      !finalJournalName
    ) {
      setError("Journal details are mandatory and must be valid.");
      return;
    }

    if (!draft.termsAccepted) {
      setError("Please accept terms and conditions before submitting.");
      return;
    }

    if (!draft.signatureDataUrl) {
      setError("Please upload or draw signature before submitting.");
      return;
    }

    setSubmitting(true);
    setError("");
    setMessage("");
    const ipMeta = await fetchPublicIpv4Meta();

    const formData = new FormData();
    const correspondenceName = (draft.correspondenceName || draft.name || "Author").trim();
    const correspondenceEmail = (draft.correspondenceEmail || draft.email || "").trim();

    const cleanName = correspondenceName.replace(/\s+/g, "_").replace(/[^\w.]/g, "");
    const shortJournalMatch = String(finalJournalName || "").match(/\(([^)]+)\)\s*$/);
    const journalSegment = (shortJournalMatch?.[1] || finalJournalName || "JOURNAL")
      .replace(/\s+/g, "_")
      .replace(/[^\w.]/g, "");
    const mySubmissionsResult = await fetchMySubmissions();
    const sequenceNumber = mySubmissionsResult.ok && Array.isArray(mySubmissionsResult.data)
      ? String(mySubmissionsResult.data.length + 1).padStart(4, "0")
      : "0001";
    const pdfFileName = `${cleanName}_${journalSegment}_${sequenceNumber}.pdf`;
    const submissionPdfBlob = await buildSubmissionPdfBlob({
      ...draft,
      name: correspondenceName,
      email: correspondenceEmail,
      ipv4Address: ipMeta.ipv4Address,
      state: ipMeta.state,
      country: ipMeta.country || draft.country,
      createdAt: new Date().toISOString()
    });
    const signatureFile = dataUrlToFile(draft.signatureDataUrl, `${cleanName || "author"}-signature.png`);
    const submissionPdfFile = new File([submissionPdfBlob], pdfFileName, { type: "application/pdf" });

    formData.append("name", draft.name || correspondenceName);
    formData.append("email", draft.email || correspondenceEmail);
    formData.append("correspondenceName", correspondenceName);
    formData.append("correspondenceEmail", correspondenceEmail);
    formData.append(
      "authorsJson",
      JSON.stringify((draft.authors || []).map((author) => ({
        ...author
      })))
    );
    formData.append("phone", draft.phone);
    formData.append("countryCode", draft.countryCode);
    formData.append("address", draft.address);
    formData.append("country", draft.country);
    formData.append("ipv4Address", ipMeta.ipv4Address || "");
    formData.append("state", ipMeta.state || "");
    formData.append("countryResolved", ipMeta.country || draft.country || "");
    formData.append("msTitle", draft.msTitle);
    formData.append("msId", draft.msId);
    formData.append("journalName", finalJournalName);
    formData.append("manuscriptType", draft.manuscriptType);
    formData.append("conflictOfInterest", draft.conflictOfInterest);
    formData.append("fundingInformation", draft.fundingInformation);
    formData.append("declaration", draft.declaration);
    formData.append("additionalNotes", draft.additionalNotes);
    formData.append("signatureMode", draft.signatureMode);
    formData.append("pdfFileName", pdfFileName);
    formData.append("selected", "false");
    formData.append("signature", signatureFile);
    formData.append("submissionPdf", submissionPdfFile);

    const result = await createSubmission(formData);
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error);
      return;
    }

    setMessage("Submission completed successfully. Record saved to user and admin dashboards.");
    resetDraft();
    setTimeout(() => navigate("/dashboard"), 500);
  };

  return (
    <div className="workspace-card form-shell">
      <div className="section-header-row">
        <h2>Signature</h2>
      </div>
      <p className="page-muted">
        Upload JPG/PNG/WEBP (max 100KB). Background will be removed automatically or draw signature.
      </p>

      <div className="signature-actions">
        <label className="toolbar-btn">
          {uploading ? "Processing..." : "Upload Signature"}
          <input type="file" accept="image/*" onChange={handleFileUpload} />
        </label>
        <button className="toolbar-btn" onClick={() => setDrawModalOpen(true)}>
          Draw Signature
        </button>
      </div>

      {draft.signatureDataUrl && (
        <div className="signature-preview-wrap">
          <button
            type="button"
            className="signature-remove-btn"
            onClick={() =>
              updateDraft({
                signatureMode: "",
                signatureDataUrl: ""
              })
            }
            aria-label="Remove signature"
            title="Remove signature"
          >
            ×
          </button>
          <p className="page-muted">Signature preview</p>
          <img src={draft.signatureDataUrl} alt="Signature preview" className="signature-preview" />
        </div>
      )}

      {message && <p className="submit-success">{message}</p>}
      {error && <p className="submit-error">{error}</p>}

      <div className="wizard-actions">
        <button className="toolbar-btn" onClick={() => navigate("/terms-and-conditions")}>
          Previous
        </button>
        <button className="toolbar-btn primary-btn" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </div>

      {drawModalOpen && (
        <SignatureCanvas
          onClose={() => setDrawModalOpen(false)}
          onSave={saveDrawnSignature}
        />
      )}
    </div>
  );
}
